<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text class="orangehrm-main-title" tag="h6">
        {{ $t('payroll.edit_earnings') }}
      </oxd-text>
      <oxd-divider />
      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <employee-autocomplete
                v-model="earning.employee"
                :rules="rules.employee"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="earning.earningName"
                :rules="rules.name"
                :label="$t('payroll.earning_name')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="earning.earningType"
                type="select"
                :rules="rules.type"
                :options="typeOptions"
                :label="$t('payroll.earning_type')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="earning.amount"
                :rules="rules.amount"
                :label="$t('payroll.earning_amount')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="earning.isPercentage"
                type="select"
                :options="percentageOptions"
                :label="$t('payroll.percentage')"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="earning.currencyId"
                type="select"
                :rules="rules.currency"
                :options="currencies"
                :label="$t('payroll.currency_id')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input
                v-model="earning.payPeriodStartDate"
                :label="$t('payroll.pay_period_start_date')"
                :rules="rules.payPeriodStartDate"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="earning.payPeriodEndDate"
                :label="$t('payroll.pay_period_end_date')"
                :rules="rules.payPeriodEndDate"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="earning.comments"
                type="textarea"
                :label="$t('general.comment')"
                :rules="rules.comments"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-button
                type="button"
                display-type="secondary"
                :label="$t('payroll.payroll_preview')"
                :loading="previewLoading"
                @click="onPreview"
              />
              <pre v-if="previewJson" class="orangehrm-pre-block">{{
                previewJson
              }}</pre>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <div class="orangehrm-button-row">
            <oxd-button
              display-type="ghost"
              :label="$t('general.cancel')"
              @click="onCancel"
            />
            <submit-button />
          </div>
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {formatEmployeeFullName} from '@/core/util/helper/employeeDisplayName';
import {navigate} from '@/core/util/helper/navigation';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import {APIService} from '@/core/util/services/api.service';
import {
  required,
  validSelection,
  validDateFormat,
  endDateShouldBeAfterStartDate,
  startDateShouldBeBeforeEndDate,
  digitsOnly,
} from '@/core/util/validation/rules';
import useDateFormat from '@/core/util/composable/useDateFormat';

const earningModel = {
  employee: null,
  earningName: '',
  earningType: null,
  amount: '',
  currencyId: '',
  isPercentage: false,
  payPeriodStartDate: '',
  payPeriodEndDate: '',
  comments: '',
};

export default {
  components: {
    'employee-autocomplete': EmployeeAutocomplete,
  },
  props: {
    payrollEarningId: {
      type: Number,
      required: true,
    },
    currencies: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const {userDateFormat} = useDateFormat();
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/earnings',
    );
    const previewHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/preview',
    );
    return {
      http,
      previewHttp,
      userDateFormat,
    };
  },
  data() {
    return {
      isLoading: false,
      previewLoading: false,
      previewJson: '',
      earning: {...earningModel},
      rules: {
        comments: [required],
        amount: [required, digitsOnly],
        name: [required],
        currency: [required],
        type: [required, validSelection],
        employee: [required, validSelection],
        payPeriodStartDate: [
          required,
          validDateFormat(this.userDateFormat),
          startDateShouldBeBeforeEndDate(
            () => this.earning.payPeriodEndDate,
            this.$t('payroll.pay_period_start_date_should_be_before_end_date'),
          ),
        ],
        payPeriodEndDate: [
          required,
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.earning.payPeriodStartDate,
            this.$t('payroll.pay_period_end_date_should_be_after_start_date'),
          ),
        ],
      },
      percentageOptions: [
        {
          id: 1,
          param: 'yes',
          label: this.$t('general.yes'),
        },
        {
          id: 2,
          param: 'no',
          label: this.$t('general.no'),
        },
      ],
      typeOptions: [
        {
          id: 1,
          param: 'bonus',
          label: this.$t('payroll.bonus'),
        },
        {
          id: 2,
          param: 'commission',
          label: this.$t('payroll.commission'),
        },
        {
          id: 3,
          param: 'allowance',
          label: this.$t('payroll.allowance'),
        },
        {
          id: 4,
          param: 'reimbursement',
          label: this.$t('payroll.reimbursement'),
        },
        {
          id: 5,
          param: 'other',
          label: this.$t('payroll.other'),
        },
      ],
    };
  },
  created() {
    this.isLoading = true;
    this.http
      .get(this.payrollEarningId)
      .then((response) => {
        const {data} = response.data;
        const isPercentage = data.isPercentage ? 'yes' : 'no';
        this.earning.employee = data.employee
          ? {
              id: data.employee.empNumber,
              label: formatEmployeeFullName(data.employee),
            }
          : null;
        this.earning.earningName = data.earningName;
        this.earning.earningType =
          this.typeOptions.find((item) => item.param === data.earningType) ||
          null;
        this.earning.amount = Number(data.amount);
        this.earning.isPercentage =
          this.percentageOptions.find((item) => item.param === isPercentage) ||
          null;
        this.earning.currencyId =
          this.currencies.find(
            (item) => item.id === data.currency.currencyId,
          ) || null;
        this.earning.payPeriodStartDate = data.payPeriodStartDate;
        this.earning.payPeriodEndDate = data.payPeriodEndDate;
        this.earning.comments = data.comments;
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    onPreview() {
      if (!this.earning.employee?.id) {
        return this.$toast.warn({
          title: this.$t('general.warning'),
          message: this.$t('general.required'),
        });
      }
      this.previewLoading = true;
      this.previewJson = '';
      this.previewHttp
        .create({
          empNumber: this.earning.employee.id,
          periodStart: this.earning.payPeriodStartDate,
          periodEnd: this.earning.payPeriodEndDate,
        })
        .then((response) => {
          this.previewJson = JSON.stringify(response.data?.data ?? {}, null, 2);
        })
        .catch(() => {
          this.$toast.error({title: this.$t('general.error')});
        })
        .finally(() => {
          this.previewLoading = false;
        });
    },
    onCancel() {
      navigate('/payroll/viewPayrollEarnings');
    },
    onSave() {
      this.isLoading = true;
      this.http
        .update(this.payrollEarningId, {
          empNumber: this.earning.employee.id,
          earningName: this.earning.earningName,
          earningType: this.earning.earningType?.param,
          amount: this.earning.amount.toString(),
          currencyId: this.earning.currencyId?.id,
          isPercentage: this.earning.isPercentage?.param === 'yes',
          payPeriodStartDate: this.earning.payPeriodStartDate,
          payPeriodEndDate: this.earning.payPeriodEndDate,
          comments: this.earning.comments,
        })
        .then(() => {
          return this.$toast.updateSuccess();
        })
        .then(() => {
          this.onCancel();
        })
        .catch((response) => {
          return this.$toast.warn({
            title: this.$t('general.warning'),
            message: response?.data.error.message,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
<style lang="scss" scoped>
.orangehrm-pre-block {
  margin-top: 1rem;
  padding: 0.75rem;
  background: #f6f6fd;
  border-radius: 0.5rem;
  max-height: 320px;
  overflow: auto;
  font-size: 12px;
}
.orangehrm-button-row {
  gap: 0.4rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  max-width: 120px;
  margin-left: 60px;

  ::v-deep(.oxd-button--medium) {
    width: 100%;
  }

  @include oxd-respond-to('md') {
    margin-left: unset;
    max-width: unset;
    ::v-deep(.oxd-button--medium) {
      width: unset;
    }
  }
}
</style>
